import { useState, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import { TextInput } from '@/components/ui/text-input'

import API from '../../../../../api/api'
import {
  selectActiveBuilding,
  selectBuildingsForFilter,
} from '../../../../../reducers/buildingSelectorReducer'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import SelectDropdownInput from '../../../../SelectDropdownInput/SelectDropdownInput'
import Stepper from '../../../../Stepper/Stepper'

const INITIAL_FORM_STATE = {
  building: null,
  resident: null,
  assignee: null,
  requestType: null,
  phase: null,
}

const RequestDetails = ({
  buildings,
  integrationProvider,
  onNext,
  onCancel,
  setIntegrationProvider,
  setBuilding,
  setAssignee,
  setResident,
  setPhase,
  setRequestType,
  activeBuilding,
  defaultResident,
  // Note: pre-selects and locks the resident (and, implicitly, the building) - used when creating
  // a request from a resident's own profile, where both are implied by context and must not be
  // changed. Distinct from `defaultResident` above, which only pre-fills (Checklists' usage
  // relies on that staying editable).
  lockResident,
  lockBuilding,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [assignees, setAssignees] = useState([])
  const [yardiPropertyIds, setYardiPropertyIds] = useState([])
  const [residents, setResidents] = useState([])
  const [isLoading, setLoading] = useState(false)
  const [isLoadingProperties, setLoadingProperties] = useState(false)

  const isYardiConfigurationSet = integrationProvider === 'yardi'
  const isEntrataConfigurationSet = integrationProvider === 'entrata'
  const isRealPageConfigurationSet = integrationProvider === 'realPage'
  const showRequestTypeSelect =
    isYardiConfigurationSet || isEntrataConfigurationSet || isRealPageConfigurationSet

  const fetchYardiPropertyIds = async (ids) => {
    try {
      setLoadingProperties(true)
      const { data = [] } = await API.getYardiPropertyIds(ids)
      setYardiPropertyIds(data)
    } catch {
      setYardiPropertyIds([])
    } finally {
      setLoadingProperties(false)
    }
  }

  useEffect(() => {
    const building = lockBuilding ?? activeBuilding
    if (building && !formState.building) {
      setFormState((prev) => ({
        ...prev,
        building: {
          ...building,
          label: building.name,
          value: building.id,
        },
      }))
    }
  }, [lockBuilding, activeBuilding, formState.building])

  useEffect(() => {
    if (lockResident && !formState.resident) {
      const clientData = {
        clientId: lockResident.id,
        clientName: lockResident.name,
        unit: lockResident.apartment ?? lockResident.buildingNumber,
      }

      setFormState((prev) => ({
        ...prev,
        requestType: 'unit',
        resident: {
          label: `${clientData.clientName} #${clientData.unit}`,
          value: clientData,
        },
      }))
      return
    }

    if (defaultResident && !formState.resident) {
      const clientName =
        defaultResident.clientName ||
        [defaultResident.firstName, defaultResident.lastName].filter(Boolean).join(' ')
      const unit = defaultResident.unit || defaultResident.apartment

      const clientData = {
        clientId: defaultResident.clientId,
        clientName,
        unit,
        sourceUnitId: defaultResident.sourceUnitId,
      }

      setFormState((prev) => ({
        ...prev,
        resident: {
          label: clientData.clientId ? `${clientData.clientName} #${clientData.unit}` : `#${unit}`,
          value: clientData,
        },
      }))
    }
  }, [lockResident, defaultResident, formState.resident])

  useEffect(() => {
    if (!formState.building) return
    const building = formState.building
    // Note: checks the raw PMS ids directly rather than the `*Config` booleans
    // (`formatBuildingsForFilter` in buildingSelectorReducer.ts) - those are only present on
    // buildings sourced from the building-filter list/`activeBuilding`, not on a plain `Building`
    // passed in via `lockBuilding` (e.g. from a Resident Profile embed), and the booleans are
    // defined as exactly this same check anyway.
    if (building.yardiManagementId && building.yardiPropertyId) {
      setIntegrationProvider('yardi')
    } else if (building.entrataManagementId && building.entrataPropertyId) {
      setIntegrationProvider('entrata')
    } else if (building.realPageManagementId && building.realPagePropertyId) {
      setIntegrationProvider('realPage')
    } else if (building.resmanPropertyId) {
      setIntegrationProvider('resman')
    } else if (building.rentManagerPropertyId) {
      setIntegrationProvider('rentManager')
    } else {
      setIntegrationProvider('native')
    }
  }, [formState.building])

  useEffect(() => {
    ;(async () => {
      if (formState.building && integrationProvider) {
        if (isYardiConfigurationSet) {
          fetchYardiPropertyIds(formState.building.id)
        }
        const assigneesResp = await API.getAssigneesForFilter(formState.building.id)

        setAssignees(
          assigneesResp.data.map((assignee) => ({
            ...assignee,
            label: `${assignee.user.firstName} ${assignee.user.lastName}`,
            value: assignee.id,
          })),
        )
      }
    })()
  }, [formState.building, integrationProvider])

  const buildingOptions = useMemo(
    () =>
      buildings
        ? buildings.map((building) => ({ ...building, value: building.id, label: building.name }))
        : [],
    [buildings],
  )

  const handleNext = () => {
    setBuilding(formState.building)
    if (formState.resident) setResident(formState.resident.value)
    setAssignee(formState.assignee)
    setPhase(formState.phase)
    setRequestType(formState.requestType)
    onNext()
  }

  const fetchResidents = useCallback(
    async (searchingQuery, isScrolling, selectedPhase) => {
      const pageSize = 100
      const skipMore = residents.length % pageSize === 0 ? residents.length : null

      if (skipMore === null && isScrolling) return null
      if (!searchingQuery) {
        setResidents([])
        setLoading(false)
        return
      }

      const fetchOptions = async (skip, query) => {
        try {
          const options = await API.getClientsOrUnits(
            formState.building.id,
            skip,
            pageSize,
            query,
            selectedPhase,
          )
          return options.map((clientData) => ({
            label: clientData.clientId
              ? `${clientData.clientName} — #${clientData.unit} ${clientData.clientEmail}`
              : `#${clientData.unit}`,
            value: clientData,
          }))
        } catch (err) {
          throw new Error(err)
        }
      }

      const actualSkip = isScrolling ? skipMore : 0
      const filteredOptions = await fetchOptions(actualSkip, searchingQuery)

      if (actualSkip === 0) {
        setResidents(filteredOptions)
      } else {
        setResidents([...residents, ...filteredOptions])
      }
      setLoading(false)
    },
    [formState.building, residents],
  )

  const loadResidentOptions = useCallback(
    async (skip, query, isScrolling) => {
      if (formState.building && formState.building.id) {
        await fetchResidents(query, isScrolling, formState.phase)
      }
    },
    [fetchResidents, formState.building],
  )

  const isMultiPhase = yardiPropertyIds.length && yardiPropertyIds.length > 1
  const isSinglePhase = isYardiConfigurationSet && !isMultiPhase

  const canGoNext = useMemo(() => {
    const { requestType, resident, phase, building } = formState

    if (isYardiConfigurationSet && building) {
      if (requestType === 'unit') {
        return !!resident && (!isMultiPhase || !!phase)
      } else if (requestType === 'commonArea') {
        return !!requestType && (!isMultiPhase || !!phase)
      }
    }

    if ((isEntrataConfigurationSet || isRealPageConfigurationSet) && building) {
      return !!resident || requestType === 'commonArea'
    }

    return building && !!resident
  }, [formState, isMultiPhase, isSinglePhase, isYardiConfigurationSet])

  return (
    <div className={`maintenance-request-modal ${isLoadingProperties ? 'is-loading' : ''}`}>
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={0}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.requestDetails.selectBuilding')}</div>
        {lockResident ? (
          <TextInput disabled readOnly value={formState.building?.label ?? ''} />
        ) : (
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={buildingOptions}
            name="building"
            value={formState.building}
            onChange={(e) => {
              setFormState((prev) => ({ ...prev, building: e, resident: null, phase: null }))
              setResidents([])
              setYardiPropertyIds([])
            }}
            placeholder={t('maintenance:form.requestDetails.buildingPlaceholder')}
          />
        )}
      </div>
      {isMultiPhase ? (
        <div className="row">
          <div className="row-header">{t('maintenance:form.requestDetails.selectPhase')}</div>
          <SelectField
            name="phase"
            value={formState.phase}
            options={yardiPropertyIds.map(({ yardiPropertyId }) => {
              return { label: yardiPropertyId, value: yardiPropertyId }
            })}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, phase: e.target.value, resident: null }))
            }
            placeholder={t('maintenance:form.requestDetails.phasePlaceholder')}
          />
        </div>
      ) : null}
      {showRequestTypeSelect && !lockResident && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.requestDetails.selectRequestType')}</div>
          <SelectField
            name="requestType"
            value={formState.requestType}
            options={[
              { label: t('maintenance:form.requestDetails.unitOption'), value: 'unit' },
              { label: t('maintenance:form.requestDetails.commonAreaOption'), value: 'commonArea' },
            ]}
            onChange={(e) => setFormState((prev) => ({ ...prev, requestType: e.target.value }))}
            placeholder={t('maintenance:form.requestDetails.requestTypePlaceholder')}
          />
        </div>
      )}
      {(showRequestTypeSelect && formState.requestType === 'unit') ||
      (!showRequestTypeSelect && formState.building) ? (
        <div className="row">
          <div className="row-header">{t('maintenance:form.requestDetails.selectUnit')}</div>
          {lockResident ? (
            <TextInput disabled readOnly value={formState.resident?.label ?? ''} />
          ) : (
            <SelectDropdownInput
              placeholder={t('maintenance:form.requestDetails.residentPlaceholder')}
              data={residents || []}
              selectedValueLabel={formState.resident ? formState.resident.label : null}
              onSelectOption={(e) => setFormState((prev) => ({ ...prev, resident: e }))}
              fetchData={loadResidentOptions}
              setLoadingState={setLoading}
              isLoading={isLoading}
              clearAfterSelect
              noResultsText={t('maintenance:form.requestDetails.residentNoResults')}
            />
          )}
        </div>
      ) : null}
      <div className="row">
        <div className="row-header">
          {t('maintenance:form.requestDetails.assignRequest')}{' '}
          <span>{t('maintenance:form.requestDetails.optional')}</span>
        </div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={assignees}
          name="assignee"
          value={formState.assignee}
          onChange={(e) => setFormState((prev) => ({ ...prev, assignee: e }))}
          placeholder={t('maintenance:form.requestDetails.assigneePlaceholder')}
        />
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray left" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button className="button button-primary" onClick={handleNext} disabled={!canGoNext}>
            {t('maintenance:form.requestDetails.next')}
          </button>
        </div>
      </div>
    </div>
  )
}

RequestDetails.propTypes = {
  buildings: PropTypes.array,
  integrationProvider: PropTypes.string,
  onNext: PropTypes.func,
  onCancel: PropTypes.func,
  setIntegrationProvider: PropTypes.func,
  setBuilding: PropTypes.func,
  setAssignee: PropTypes.func,
  setResident: PropTypes.func,
  setPhase: PropTypes.func,
  setRequestType: PropTypes.func,
  activeBuilding: PropTypes.object,
  defaultResident: PropTypes.object,
  lockResident: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    apartment: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    buildingNumber: PropTypes.string,
  }),
  lockBuilding: PropTypes.object,
}

export default connect((state) => ({
  activeBuilding: selectActiveBuilding(state),
  buildings: selectBuildingsForFilter(state),
}))(RequestDetails)
